import { useCallback, useEffect, useState } from "react";
import { Timer, Loader2, Trophy, AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell,
} from "recharts";
import { getMetricasTempoResposta } from "@/lib/api";
import { ChartTooltipFlua, fluaGridProps, fluaAxisProps } from "@/components/ChartTooltipFlua";

// Gráfico do tempo médio (min) que cada vendedor leva pra responder um cadastro
// recebido pelo formulário público. Linha de referência = SLA de 30 min.
export default function TempoRespostaOnboardingCard() {
  const [data, setData] = useState({ sla_minutos: 30, vendedores: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getMetricasTempoResposta();
      setData(r || { sla_minutos: 30, vendedores: [] });
    } catch (e) {
      console.error("Falha ao carregar métricas de resposta:", e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const onValid = () => load();
    window.addEventListener("onboarding:validated", onValid);
    return () => window.removeEventListener("onboarding:validated", onValid);
  }, [load]);

  const sla = data.sla_minutos || 30;
  const rows = data.vendedores || [];
  const media = rows.length
    ? rows.reduce((s, v) => s + Number(v.tempo_medio_min || 0), 0) / rows.length
    : 0;
  const dentroSla = rows.reduce((s, v) => s + Number(v.dentro_sla || 0), 0);
  const totalRespostas = rows.reduce((s, v) => s + Number(v.total_respostas || 0), 0);
  const slaPct = totalRespostas ? Math.round((100 * dentroSla) / totalRespostas) : 0;

  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-4" data-testid="tempo-resposta-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/15 grid place-items-center shrink-0">
            <Timer className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-lg">Tempo médio de resposta ao cliente</h3>
            <p className="text-xs text-muted-foreground mt-0.5 max-w-xl">
              Do envio do formulário público até o vendedor confirmar o envio do link de assinatura. Meta SLA: <strong>{sla} min</strong>.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="tabular-nums">
            Média geral: <span className="ml-1 font-bold text-primary">{media.toFixed(1)} min</span>
          </Badge>
          <Badge variant="outline" className={slaPct >= 70 ? "text-emerald-500 border-emerald-500/40" : "text-amber-500 border-amber-500/40"}>
            {slaPct}% dentro do SLA
          </Badge>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10 text-muted-foreground flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Calculando…
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-10 text-sm text-muted-foreground">
          Ainda não há respostas registradas. Quando os vendedores marcarem os cadastros como respondidos, o gráfico aparece aqui.
        </div>
      ) : (
        <>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
                <CartesianGrid {...fluaGridProps} />
                <XAxis dataKey="vendedor" {...fluaAxisProps} />
                <YAxis
                  {...fluaAxisProps}
                  label={{ value: "min", angle: -90, position: "insideLeft", fill: "hsl(var(--muted-foreground))", fontSize: 10 }}
                />
                <Tooltip
                  cursor={{ fill: "hsl(var(--primary) / 0.06)" }}
                  content={<ChartTooltipFlua valueFormatter={(v) => `${v} min`} />}
                />
                <ReferenceLine y={sla} stroke="hsl(var(--primary))" strokeDasharray="4 4"
                  label={{ value: `Meta ${sla}min`, position: "right", fill: "hsl(var(--primary))", fontSize: 10 }}
                />
                <Bar dataKey="tempo_medio_min" radius={[6, 6, 0, 0]} isAnimationActive={false}>
                  {rows.map((r, i) => (
                    <Cell key={i} fill={r.tempo_medio_min <= sla ? "#10b981" : r.tempo_medio_min <= sla * 1.5 ? "#f59e0b" : "#ef4444"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {rows.map((r, idx) => {
              const dentro = r.tempo_medio_min <= sla;
              const isPodium = idx === 0 && dentro;
              return (
                <div key={r.vendedor}
                  className={`rounded-lg border p-3 flex items-center gap-3 ${
                    isPodium ? "border-primary/40 bg-primary/5" :
                    dentro ? "border-emerald-500/30 bg-emerald-500/5" :
                    "border-amber-500/30 bg-amber-500/5"
                  }`}
                  data-testid={`sla-row-${r.vendedor}`}
                >
                  <div className={`w-8 h-8 rounded-md grid place-items-center shrink-0 ${
                    isPodium ? "bg-primary/20 text-primary" :
                    dentro ? "bg-emerald-500/20 text-emerald-500" :
                    "bg-amber-500/20 text-amber-500"
                  }`}>
                    {isPodium ? <Trophy className="w-4 h-4" /> :
                     dentro ? <Timer className="w-4 h-4" /> :
                     <AlertTriangle className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate text-sm">{r.vendedor}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {r.total_respostas} resposta{r.total_respostas !== 1 ? "s" : ""} · SLA {r.sla_pct}%
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`font-heading text-xl font-extrabold tabular-nums ${
                      dentro ? "text-emerald-500" : "text-amber-500"
                    }`}>{r.tempo_medio_min}<span className="text-[10px] font-normal ml-0.5">min</span></div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
